import React from 'react'
import Header from '../components/layouts/Header'
import Footer from '../components/layouts/Footer'
import useCanonical from '../hooks/useCanonical'

const PrivacyPolicy = () => {
    const canonicalTag = useCanonical('/privacy-policy');
    
    return (
        <div className='bg-black text-white'>
            {canonicalTag}
            <Header />
            <PrivacyPolicyContent />
            <Footer />
        </div>
    );
}

const PrivacyPolicyContent = () => {
    return (
        <div className="--layout-padding !py-[30px]">
            <h2 className="text-xl font-semibold mt-6 mb-2">1. Introduction</h2>
            <p className="mb-4">Thank you for choosing <strong>Dev-Hire.com</strong>, a platform operated by Acveti Inc. ("Dev-Hire," "we," "our," or "us"). We are committed to protecting your personal data and upholding your privacy rights. This Privacy Policy explains how we collect, use, and safeguard the information you provide to us when using our website and services.</p>
            <p className="mb-4">Dev-Hire provides full-time remote software developers, employed by us, to businesses worldwide. Our developers are rigorously vetted and verified through a combination of internal processes and third-party verification services. We connect clients with highly skilled, time-zone-aligned, full-time professionals across 100+ technologies.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">2. Scope</h2>
            <p className="mb-2">This Privacy Policy applies to all users of our services, including but not limited to:</p>
            <ul className="list-disc list-inside mb-4">
                <li>Developers applying to work with or currently employed by Dev-Hire</li>
                <li>Clients and prospective clients</li>
                <li>Visitors to our website</li>
                <li>Job applicants</li>
            </ul>
            <p className="mb-4">It covers how we collect, retain, and use information, including personal data ("Personal Data"), and the rights you have regarding your information.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">3. Collection and Use of Personal Data</h2>
            <h3 className="font-semibold mt-4 mb-1">a) Developers</h3>
            <p className="mb-2">Information We Collect:</p>
            <ul className="list-disc list-inside mb-2">
                <li>Name, contact details, location</li>
                <li>Resume/CV, work experience, education, and references</li>
                <li>Government-issued identification</li>
                <li>Recorded interviews and assessments</li>
                <li>Internal evaluations and performance feedback</li>
                <li>Verification data, including photos and ID documents, during onboarding (reviewed by internal teams and third-party partners)</li>
            </ul>
            <p className="mb-2">How We Use the Information:</p>
            <ul className="list-disc list-inside mb-2">
                <li>To evaluate and verify candidates</li>
                <li>To manage employment and assignments</li>
                <li>For internal training and quality assurance</li>
                <li>To showcase developer profiles to clients (only with consent)</li>
                <li>To ensure compliance with legal and regulatory requirements</li>
            </ul>
            <p className="mb-2">Recordings: Video/audio interviews and client interactions may be recorded with prior notice and stored for training, evaluation, or compliance.</p>

            <h3 className="font-semibold mt-4 mb-1">b) Clients and Prospective Clients</h3>
            <p className="mb-2">Information We Collect:</p>
            <ul className="list-disc list-inside mb-2">
                <li>Name, email, phone, company name, job title</li>
                <li>Project requirements, technology needs, and hiring goals</li>
                <li>Billing and payment information (via Stripe)</li>
            </ul>
            <p className="mb-2">How We Use the Information:</p>
            <ul className="list-disc list-inside mb-2">
                <li>To match clients with suitable developers</li>
                <li>To manage project onboarding, delivery, and support</li>
                <li>For billing and administrative communication</li>
                <li>For promotional and marketing purposes (you may opt-out)</li>
            </ul>
            <p className="mb-2">Recordings: Client calls may be recorded with notice to understand project requirements and ensure service quality.</p>

            <h3 className="font-semibold mt-4 mb-1">c) Job Applicants</h3>
            <p className="mb-2">We collect personal and professional information from individuals applying for positions at Dev-Hire. Data includes resumes, application details, salary expectations, and assessment results.</p>
            <p className="mb-2">Use of Data:</p>
            <ul className="list-disc list-inside mb-2">
                <li>To assess suitability for employment</li>
                <li>To maintain recruitment records</li>
                <li>To comply with legal obligations</li>
            </ul>

            <h3 className="font-semibold mt-4 mb-1">d) Website Visitors</h3>
            <p className="mb-2">We use cookies and similar tracking technologies, including Google Analytics, to collect:</p>
            <ul className="list-disc list-inside mb-2">
                <li>IP address, browser type, operating system</li>
                <li>Browsing behavior on our website</li>
                <li>Device identifiers</li>
            </ul>
            <p className="mb-2">We also use advertising cookies (e.g., Facebook Pixel, Google Ads) to deliver personalized content and improve our marketing efforts. You can manage cookie preferences in your browser settings.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">4. Data Sharing and Disclosure</h2>
            <p className="mb-2">We do not sell your data. We may share your information in the following cases:</p>
            <ul className="list-disc list-inside mb-2">
                <li>With our affiliates and service providers who assist us in verification, hosting, analytics, marketing, and operations</li>
                <li>With Stripe for payment processing</li>
                <li>With clients (only relevant developer details upon engagement)</li>
                <li>As required by law, to comply with legal processes, protect our rights, or ensure platform integrity</li>
                <li>In case of a merger, acquisition, or business transfer</li>
            </ul>

            <h2 className="text-xl font-semibold mt-6 mb-2">5. Data Retention</h2>
            <p className="mb-4">We retain your data as long as necessary to:</p>
            <ul className="list-disc list-inside mb-2">
                <li>Provide our services</li>
                <li>Fulfill contractual obligations</li>
                <li>Comply with legal requirements</li>
                <li>Maintain platform security and fraud prevention</li>
            </ul>
            <p className="mb-4">Anonymized or aggregated data may be retained longer.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">6. International Data Transfers</h2>
            <p className="mb-4">Your Personal Data may be transferred and processed outside of your jurisdiction, including in the United States, India, or other locations where Dev-Hire operates. We implement appropriate safeguards in accordance with GDPR and other applicable data protection laws.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">7. Your Rights (EU, UK & California Residents)</h2>
            <p className="mb-2">You may have the right to:</p>
            <ul className="list-disc list-inside mb-2">
                <li>Access or correct your Personal Data</li>
                <li>Request deletion or restrict processing</li>
                <li>Withdraw consent (where applicable)</li>
                <li>Opt out of marketing communications</li>
                <li>File complaints with regulatory authorities</li>
            </ul>
            <p className="mb-2">California residents may also request:</p>
            <ul className="list-disc list-inside mb-2">
                <li>Disclosure of data categories shared or collected</li>
                <li>Deletion of personal data</li>
                <li>No discrimination for exercising privacy rights</li>
            </ul>
            <p className="mb-2">To exercise your rights, please contact us at: <a href="mailto:privacy@dev-hire.com" className="text-blue-600 underline">privacy@dev-hire.com</a></p>

            <h2 className="text-xl font-semibold mt-6 mb-2">8. Security</h2>
            <p className="mb-4">We implement commercially reasonable measures to secure your data against unauthorized access, loss, or misuse. However, no digital transmission is 100% secure.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">9. Children's Privacy</h2>
            <p className="mb-4">Dev-Hire.com is not intended for children under the age of 16. We do not knowingly collect data from minors. If you believe a child has provided personal data, please contact us for immediate removal.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">10. Third-Party Links</h2>
            <p className="mb-4">Our site may contain links to third-party websites. We are not responsible for the privacy practices of those sites. Please review their privacy policies before submitting any information.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">11. Changes to this Privacy Policy</h2>
            <p className="mb-4">We may update this Privacy Policy from time to time to reflect changes in our operations or legal requirements. The latest version will be posted on Dev-Hire.com with the updated effective date. Your continued use of our services signifies acceptance of the changes.</p>

            <h2 className="text-xl font-semibold mt-6 mb-2">12. Contact Us</h2>
            <p className="mb-4">If you have any questions or concerns about this Privacy Policy or how your data is handled, contact:</p>
            <address className="not-italic mb-4">
                Acveti Inc.<br />
                1178 Broadway, 3rd Floor<br />
                New York, NY, Manhattan, US, 10001<br />
                Email: <a href="mailto:privacy@dev-hire.com" className="text-blue-600 underline">privacy@dev-hire.com</a>
            </address>
        </div>
    )
}
export default PrivacyPolicy